/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Grid,
  IconButton,
  LinearProgress,
  List,
  MenuItem,
  ToggleButton,
} from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';

import { RegistrationUf } from '../../../../../api/enumerations';
import { getCities } from '../../../../../api/theHive/cities';
import { CitiesData } from '../../../../../api/theHive/cities/types';
import { listLicensedCompanies } from '../../../../../api/theHive/licensedCompanies';
import { LicensedCompanyData } from '../../../../../api/theHive/licensedCompanies/types';
import {
  ColoredCircularProgress,
  menuItemDefaultStyle,
  StyledLinearProgress,
} from '../../../../../components/UI/Typography';
import { IconCloseMS, IconSearchMS } from '../../../../../constants/icons';
import { selectRegistrationUf } from '../../../../../constants/selectOptions';
import { GlobalContext } from '../../../../../context/global';
import useErrorMessage from '../../../../../hooks/useErrorMessage';
import { SelectedCompany } from './SelectedCompany';
import {
  ListCompanies,
  ListItemStyled,
  SearchTextField,
  StyledInputAdornment,
  StyledInputBase,
  StyledToggleButtonGroup,
} from './styles';

export function Location(): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [active, setActive] = useState<string | null>('active');
  const [selectedCompany, setSelectedCompany] = useState<
    LicensedCompanyData | undefined
  >();
  const [uf, setUf] = useState(0);
  const [loadingCities, setLoadingCities] = useState(false);
  const [cities, setCities] = useState<CitiesData[]>([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [searchCompany, setSearchCompany] = useState('');
  const [licensedCompanies, setLicensedCompanies] = useState<
    LicensedCompanyData[]
  >([]);

  const { getErrorMessage } = useErrorMessage();

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);

  const getDataCallback = useCallback(
    async (citySearch?: number) => {
      setLoadingSearch(true);

      let isActive;
      if (active === 'active') isActive = true;
      if (active === 'inactive') isActive = false;

      try {
        const response = await listLicensedCompanies(
          1,
          100,
          searchCompany,
          citySearch && citySearch,
          isActive
        );

        if (response.detail.description) {
          throw new Error(response.detail.description);
        }

        if (!response.data) {
          throw new Error('Algo deu errado, tente novamente.');
        }

        setLicensedCompanies(response.data);
        if (response.data.length > 0) setSelectedCompany(response.data[0]);
        else setSelectedCompany(undefined);
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error));
        setErrorMessage(true);
        setOpenSnackbar(true);
      } finally {
        setLoading(false);
        setLoadingSearch(false);
      }
    },
    [searchCompany, active]
  );

  useEffect(() => {
    if (searchCompany.length > 0) {
      const delayDebounceFn = setTimeout(() => {
        getDataCallback();
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    }
    getDataCallback();
    return undefined;
  }, [searchCompany, active]);

  const handleUfSearch = useCallback(async () => {
    setLoadingCities(true);
    try {
      const response = await getCities(uf);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (!response.data) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setCities(response.data);
      if (response.data.length > 0) setSelectedCity(response.data[0].name);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setLoadingCities(false);
    }
  }, [uf]);

  useEffect(() => {
    setCities([]);
    if (uf !== RegistrationUf.SELECT) {
      handleUfSearch();
    }
  }, [uf, handleUfSearch]);

  const handleActive = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string | null
  ): void => {
    setActive(newValue);
  };

  return (
    <Grid container spacing={2} mt={2}>
      {loading ? (
        <Grid item xs={12}>
          <StyledLinearProgress />
        </Grid>
      ) : (
        <>
          <Grid item xs={5}>
            <ListCompanies>
              <Box display="flex" gap={2} mb={2}>
                <StyledInputBase
                  placeholder="buscar por documento"
                  inputProps={{ 'aria-label': 'pesquisar', maxLength: 18 }}
                  type="search"
                  startAdornment={
                    <StyledInputAdornment position="start">
                      {IconSearchMS}
                    </StyledInputAdornment>
                  }
                  onChange={(e) => {
                    if (e.target.value.length > 0) {
                      setSearchCompany(e.target.value);
                    }
                    if (e.target.value.length === 0) {
                      setSearchCompany('');
                    }
                  }}
                  onFocus={(e) => {
                    e.target.value = '';
                    setSearchCompany('');
                  }}
                />
                <StyledToggleButtonGroup
                  value={active}
                  exclusive
                  onChange={handleActive}
                  aria-label="ativo"
                >
                  <ToggleButton value="active" aria-label="active">
                    ativo
                  </ToggleButton>
                  <ToggleButton value="inactive" aria-label="inactive">
                    inativo
                  </ToggleButton>
                </StyledToggleButtonGroup>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <SearchTextField
                  id="uf"
                  label="UF"
                  value={uf}
                  select
                  onChange={(e) => {
                    setUf(Number(e.target.value));
                  }}
                >
                  {selectRegistrationUf().map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={menuItemDefaultStyle}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </SearchTextField>
                {cities.length > 0 && (
                  <>
                    <SearchTextField
                      id="city"
                      label="cidade"
                      value={selectedCity}
                      select
                      onChange={(e) => {
                        setSelectedCity(e.target.value);
                        const city = cities.filter(
                          (c) => c.name === e.target.value
                        );
                        if (city.length) getDataCallback(city[0].id);
                      }}
                      sx={{ width: '100%' }}
                    >
                      {cities.map((option) => (
                        <MenuItem
                          key={option.id}
                          value={option.name}
                          sx={menuItemDefaultStyle}
                        >
                          {option.name}
                        </MenuItem>
                      ))}
                    </SearchTextField>
                    <IconButton
                      onClick={() => {
                        setUf(0);
                        setCities([]);
                        getDataCallback();
                      }}
                    >
                      {IconCloseMS}
                    </IconButton>
                  </>
                )}
                {loadingCities && <ColoredCircularProgress size={22} />}
              </Box>
              {loadingSearch ? (
                <LinearProgress sx={{ margin: '20px' }} />
              ) : (
                <List>
                  {licensedCompanies.length > 0
                    ? licensedCompanies.map((data) => (
                        <ListItemStyled
                          key={data.id}
                          className={
                            data.company.name === selectedCompany?.company.name
                              ? 'active'
                              : ''
                          }
                          onClick={() => setSelectedCompany(data)}
                        >
                          <img
                            src={data.company.logo_url}
                            alt={data.company.name}
                          />
                          {data.company.name}
                        </ListItemStyled>
                      ))
                    : 'nenhuma empresa encontrada'}
                </List>
              )}
            </ListCompanies>
          </Grid>
          {selectedCompany && (
            <Grid item xs={7}>
              <SelectedCompany selectedCompany={selectedCompany} />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
}
